import { useEffect, useState } from "react";
import { Loader2, Save, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";

const ACSA_EMAIL_DEFAULTS = {
  acsa_email_to: "jgodfrey@acsa.org",
  acsa_email_cc: "",
  acsa_email_subject: "ACSA Invoice for {client_name}",
  acsa_email_body: "Hello Joanne,\n\nI've attached the ACSA invoice for client {client_name}.\n\nBest regards,\nBrad Kasten\nLegal Assistant\nKasten@LawSpecialties.com\n(858) 682-5400",
};

function AcsaEmailEditor() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then((r) => setForm({
        acsa_email_to: r.data.acsa_email_to ?? ACSA_EMAIL_DEFAULTS.acsa_email_to,
        acsa_email_cc: r.data.acsa_email_cc ?? ACSA_EMAIL_DEFAULTS.acsa_email_cc,
        acsa_email_subject: r.data.acsa_email_subject ?? ACSA_EMAIL_DEFAULTS.acsa_email_subject,
        acsa_email_body: r.data.acsa_email_body ?? ACSA_EMAIL_DEFAULTS.acsa_email_body,
      }))
      .catch(() => setForm({ ...ACSA_EMAIL_DEFAULTS }));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/settings", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="acsa-email-editor">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="acsa-set-to">To</Label>
          <Input id="acsa-set-to" data-testid="acsa-email-to-input" value={form.acsa_email_to} onChange={set("acsa_email_to")} placeholder="jgodfrey@acsa.org" />
        </div>
        <div>
          <Label htmlFor="acsa-set-cc">CC</Label>
          <Input id="acsa-set-cc" data-testid="acsa-email-cc-input" value={form.acsa_email_cc} onChange={set("acsa_email_cc")} placeholder="Linda's email (optional)" />
        </div>
      </div>
      <div>
        <Label htmlFor="acsa-set-subject">Subject template</Label>
        <Input id="acsa-set-subject" data-testid="acsa-email-subject-input" value={form.acsa_email_subject} onChange={set("acsa_email_subject")} />
      </div>
      <div>
        <Label htmlFor="acsa-set-body">Body template</Label>
        <textarea id="acsa-set-body" data-testid="acsa-email-body-input" value={form.acsa_email_body} onChange={set("acsa_email_body")} rows={9}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40" />
      </div>
      <p className="-mt-1 text-xs text-muted-foreground">
        Available merge field: <code className="rounded bg-secondary px-1">{"{client_name}"}</code>. The matter&apos;s frozen ACSA invoice PDF is attached automatically when you send.
      </p>
      <div className="flex items-center gap-3">
        {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
        <div className="ml-auto">
          <Button size="sm" onClick={save} disabled={saving} data-testid="acsa-email-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save ACSA email
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AcsaEmailCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>ACSA Submission Email</CardTitle>
        <CardDescription>Defaults for the &ldquo;Send to ACSA&rdquo; action that emails a frozen ACSA invoice PDF to the funder. Editable per-send. Merge field: {"{client_name}"}.</CardDescription>
      </CardHeader>
      <CardContent><AcsaEmailEditor /></CardContent>
    </Card>
  );
}
